import React from 'react';
import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Users, CreditCard, DollarSign, ArrowLeft, LayoutList, Zap, Phone, Shield, KeyRound, History, LayoutGrid, CircuitBoard, Gauge, Waypoints } from 'lucide-react';

const NAV_ITEMS = [
    { to: '/admin/feature-access', label: 'Feature Access', icon: Shield },
    { to: '/admin/role-permissions', label: 'Role Permissions', icon: KeyRound },
    { to: '/admin/audit-log', label: 'Audit Log', icon: History },
    { to: '/admin/metrics-gallery', label: 'Metrics Gallery', icon: LayoutGrid },
    { to: '/admin/billing', label: 'Billing Accounts', icon: CreditCard },
    { to: '/admin/pricing', label: 'Pricing Rules', icon: DollarSign },
    { to: '/admin/plans', label: 'Plans', icon: LayoutList },
    { to: '/admin/phone-numbers', label: 'Phone Numbers', icon: Phone },
    { to: '/admin/stripe-events', label: 'Stripe Events', icon: Zap },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/dispatch-breakers', label: 'Dispatch Breakers', icon: CircuitBoard },
    { to: '/admin/rate-limits', label: 'Rate Limits', icon: Gauge },
    { to: '/admin/call-limits', label: 'Call Concurrency', icon: Gauge },
    { to: '/admin/turn-taking', label: 'Turn-Taking', icon: Waypoints },
    { to: '/admin/stress-admission', label: 'Stress Admission', icon: Gauge },
];

const AdminLayout = () => {
    const { user, isAuthenticated, isInitialized } = useAuth();

    // Wait for auth to initialize before checking
    if (!isInitialized) {
        return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>;
    }

    // Redirect unauthenticated users to login
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Only internal admins (rubrichq staff by email allowlist — see
    // User#internal_admin? on the backend) may access the admin portal.
    // The flag is derived server-side from email and included in the login
    // response alongside user.roles.
    const isInternalAdmin = !!user?.internal_admin;
    if (!isInternalAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="flex h-screen bg-muted/50">
            {/* Sidebar */}
            <div className="w-60 bg-card border-r border-border flex flex-col">
                <div className="p-4 border-b border-border">
                    <h1 className="text-lg font-bold text-foreground">Admin Portal</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Internal Management</p>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'bg-gray-900 text-white'
                                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                }`
                            }
                        >
                            <Icon className="h-5 w-5" />
                            {label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-border">
                    <NavLink
                        to="/dashboard"
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to App
                    </NavLink>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 overflow-y-auto p-4">
                <Outlet />
            </div>
        </div>
    );
};

export default AdminLayout;
